/**
 * Identity of the installation this build is serving.
 *
 * CRDB runs two physically separate installations: one for Tanzania and
 * Burundi, one standalone for DR Congo. They share a codebase and nothing
 * else — separate database, EDMS namespace, WSO2 gateway, log stream and DR
 * site — because DRC data may not transit Tanzania.
 *
 * The identity therefore comes from deployment configuration, never from the
 * signed-in user or the URL: an installation cannot be talked out of which
 * country it is by anything arriving in a request.
 */
export type CountryCode = 'TZ' | 'CD';

export interface Installation {
    id: string;
    tenantCode: string;
    /** Countries this installation is licensed to hold data for. */
    countries: { code: CountryCode | 'BI'; name: string }[];
    primaryCountry: CountryCode;
    jurisdiction: string;
    dataCentre: string;
    database: string;
    edmsNamespace: string;
    integrationGateway: string;
    logStream: string;
    disasterRecovery: string;
    locale: 'en' | 'fr';
}

const INSTALLATIONS: Record<string, Installation> = {
    TZ: {
        id: 'Installation A',
        tenantCode: 'CRDB-TZ-001',
        countries: [
            { code: 'TZ', name: 'Tanzania' },
            { code: 'BI', name: 'Burundi' },
        ],
        primaryCountry: 'TZ',
        jurisdiction: 'Bank of Tanzania',
        dataCentre: 'Dar es Salaam — CRDB primary data centre',
        database: 'tradefinance_tz',
        edmsNamespace: 'edms://crdb-tz/trade-finance',
        integrationGateway: 'wso2-tz.crdb.internal',
        logStream: 'audit.tz.trade-finance',
        disasterRecovery: 'Dodoma — CRDB secondary site',
        locale: 'en',
    },
    CD: {
        id: 'Installation B',
        tenantCode: 'CRDB-CD-001',
        countries: [{ code: 'CD', name: 'RD Congo' }],
        primaryCountry: 'CD',
        jurisdiction: 'Banque Centrale du Congo',
        dataCentre: 'Kinshasa — CRDB DRC data centre',
        database: 'tradefinance_cd',
        edmsNamespace: 'edms://crdb-cd/trade-finance',
        integrationGateway: 'wso2-cd.crdb.internal',
        logStream: 'audit.cd.trade-finance',
        disasterRecovery: 'Lubumbashi — CRDB DRC secondary site',
        locale: 'fr',
    },
};

export const getInstallation = (): Installation => {
    let code = (process.env.REACT_APP_INSTALLATION_COUNTRY as string) || 'TZ';

    // Mirrors the theme switch: lets a developer run either installation
    // locally without a rebuild. Production identity is the env var alone.
    if (process.env.NODE_ENV === 'development') {
        code = localStorage?.getItem('REACT_APP_INSTALLATION_COUNTRY') || code;
    }

    return INSTALLATIONS[code] || INSTALLATIONS.TZ;
};

export const installationFor = (code: string): Installation | undefined => INSTALLATIONS[code];

export const otherInstallation = (current: Installation): Installation =>
    current.primaryCountry === 'TZ' ? INSTALLATIONS.CD : INSTALLATIONS.TZ;
